import { PAccordion, PText } from '@porsche-design-system/components-react';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { isValidVersion } from '../utils/versionUtils';
import { EpicFlyout } from './EpicFlyout';
import { HelpModal } from './HelpModal';
import { IssueCard } from './IssueCard';

/* Todo: slice inner components */

const colClassMap = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  5: 'grid-cols-5',
  6: 'grid-cols-6',
  7: 'grid-cols-7',
  8: 'grid-cols-8',
  9: 'grid-cols-9',
  10: 'grid-cols-10',
};

export const Epicmap = memo(({ issues, versions, expandState }) => {
  const [openFlyout, setOpenFlyout] = useState(false);
  const [flyoutData, setFlyoutData] = useState({});
  const [flyoutEpicName, setFlyoutEpicName] = useState('');
  const [expandedAccordions, setExpandedAccordions] = useState({});

  const gridVersions = useMemo(() => ['Unassigned', ...versions], [versions]);
  const hasIssues = issues?.length > 0;

  const projectEpicIssues = useMemo(() => {
    const map = {};

    issues.forEach((issue) => {
      const epicLink = issue.fields?.issuelinks?.find(
        (link) => link.outwardIssue?.fields.issuetype.name === 'Epic' ||
          link.outwardIssue?.fields.issuetype.name === 'Portfolio Epic'
      );
      const epicName = epicLink?.outwardIssue.fields.summary || 'Unassigned';
      const projectName = issue.fields.project?.name || 'Unassigned';

      const validVersions = (issue.fields.versions || []).map((v) => v.name).filter(isValidVersion);
      const usedVersions = validVersions.length > 0 ? validVersions : ['Unassigned'];

      if (!map[projectName]) map[projectName] = {};
      if (!map[projectName][epicName]) map[projectName][epicName] = {};

      usedVersions.forEach((version) => {
        const v = versions.includes(version) ? version : 'Unassigned';
        if (!map[projectName][epicName][v]) {
          map[projectName][epicName][v] = [];
        }
        map[projectName][epicName][v].push(issue);
      });
    });

    return {
      projects: Object.entries(map).map(([projectName, epics]) => ({
        projectName,
        epics: Object.entries(epics).map(([epicName, versionMap]) => {
          const versionKeys = Object.keys(versionMap).filter((v) => gridVersions.includes(v));
          versionKeys.sort((a, b) => gridVersions.indexOf(a) - gridVersions.indexOf(b));

          const startIndex = gridVersions.indexOf(versionKeys[0]);
          const endIndex = gridVersions.indexOf(versionKeys[versionKeys.length - 1]);

          return {
            epicName,
            startIndex,
            endIndex,
            cols: endIndex - startIndex + 1,
            issues: versionMap,
          };
        }),
      })),
    };
  }, [gridVersions, issues, versions]);

  useEffect(() => {
    if (!expandState || expandState === 'none') return;

    const newExpanded = {};
    projectEpicIssues.projects.forEach((project) => {
      project.epics.forEach((epic) => {
        newExpanded[epic.epicName] = expandState === 'expanded';
      });
    });

    setExpandedAccordions(newExpanded);
  }, [expandState, projectEpicIssues]);

  if (!hasIssues) {
    return <HelpModal />;
  }

  return (
    <>
      <div className="relative">
        {/* Vertical lines grid background */}
        <div
          className="absolute top-0 left-[40px] right-0 bottom-0 z-0 pointer-events-none grid"
          style={{
            gridTemplateColumns: `minmax(240px, 1fr) repeat(${versions.length}, minmax(240px, 1fr))`,
          }}
        >
          {[...Array(versions.length + 1)].map((_, i) => (
            <div key={i} className="border-r border-gray-300" />
          ))}
        </div>

        {/* Header row */}
        <div
          className="grid auto-rows-auto text-center relative z-10"
          style={{
            gridTemplateColumns: `40px minmax(240px, 1fr) repeat(${versions.length}, minmax(240px, 1fr))`,
          }}
        >
          <div />
          <div className="border-r border-gray-300 py-2">
            <PText align="center" size="small" weight="semi-bold">
              Unassigned
            </PText>
          </div>
          {versions.map((version) => (
            <div key={version} className="border-r border-gray-300 py-2">
              <PText align="center" size="small" weight="semi-bold">
                {version}
              </PText>
            </div>
          ))}
        </div>

        {/* Epic rows */}
        <div
          className="grid auto-rows-auto text-center relative z-10"
          style={{
            gridTemplateColumns: `40px minmax(240px, 1fr) repeat(${versions.length}, minmax(240px, 1fr))`,
          }}
        >
          {projectEpicIssues.projects.map((project) => (
            <React.Fragment key={project.projectName}>
              <div className="col-span-full border-b border-gray-300" />
              <div
                className="flex items-center justify-center row-span-1"
                style={{ gridColumn: '1 / span 1' }}
              >
                <span className="[writing-mode:vertical-rl] rotate-180">
                  <PText size="xx-small">{project.projectName}</PText>
                </span>
              </div>

              {project.epics.map((epicData, i) => {
                const gridColsClass = colClassMap[epicData.cols] || 'grid-cols-1';
                return (
                  <div
                    key={i}
                    className="px-2 py-2"
                    style={{ gridColumn: `${epicData.startIndex + 2} / span ${epicData.cols}` }}
                  >
                    <div className="w-full rounded-xl border border-white/20 backdrop-blur-md bg-white/10 shadow-lg p-4">
                      <PText
                        className="cursor-pointer"
                        size="x-small"
                        weight="semi-bold"
                        onClick={() => {
                          setFlyoutData(epicData.issues);
                          setFlyoutEpicName(epicData.epicName);
                          setOpenFlyout(true);
                        }}
                      >
                        {epicData.epicName}
                      </PText>
                      <PAccordion
                        heading={epicData.epicName}
                        headingTag="h3"
                        open={!!expandedAccordions[epicData.epicName]}
                        onUpdate={() =>
                          setExpandedAccordions((prev) => ({
                            ...prev,
                            [epicData.epicName]: !prev[epicData.epicName],
                          }))
                        }
                      >
                        <div className={`grid ${gridColsClass} gap-4`}>
                          {Array.from({ length: epicData.cols }).map((_, colIndex) => {
                            const absoluteVersionIndex = epicData.startIndex + colIndex;
                            const version = gridVersions[absoluteVersionIndex];
                            const versionIssues = epicData.issues[version] || [];

                            return (
                              <div key={version} className="space-y-2">
                                <PText size="xx-small" align="center">
                                  {version}
                                </PText>
                                {versionIssues.map((issue) => (
                                  <IssueCard key={issue.key} issue={issue} />
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      </PAccordion>
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      {openFlyout && (
        <EpicFlyout
          onClose={() => setOpenFlyout(false)}
          epicName={flyoutEpicName}
          flyoutData={flyoutData}
          versions={versions}
        />
      )}
    </>
  );
});
